import type { QueryKey } from '@tanstack/react-query';

import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { GetSnippetPathsParams, SnippetPathsResponse } from '../../../../shared/snippets';

import { type CompareSnippetsParams, getSnippetsBaseQueryKey } from './shared';

/**
 * Options for the useSnippetPaths hook.
 */
export type UseSnippetPathsOptions = {
  userRevisionLocator: string;
  params: GetSnippetPathsParams;
  compare?: CompareSnippetsParams;
};

/**
 *  Function that fetches snippet paths based on the given options.
 */
export function getSnippetPaths({ userRevisionLocator, params, compare }: UseSnippetPathsOptions) {
  return compare
    ? get<SnippetPathsResponse>(`/api/revisions/${nuc(userRevisionLocator)}/snippets/compare/${nuc(compare.olderRevisionLocator)}/${compare.status}/paths`, false, params)
    : get<SnippetPathsResponse>(`/api/revisions/${nuc(userRevisionLocator)}/snippets/paths`, false, params);
}

/**
 * Returns the query key for the snippet paths based on the given options.
 */
export const getSnippetPathsQueryKey = ({ userRevisionLocator, params, compare }: UseSnippetPathsOptions): QueryKey => {
  return compare
    ? [...getSnippetsBaseQueryKey(userRevisionLocator), 'compare', compare.olderRevisionLocator, compare.status, 'paths', params]
    : [...getSnippetsBaseQueryKey(userRevisionLocator), 'paths', params];
};

/**
 * useSnippetPaths is a hook that fetches snippet paths.
 */
export default function useSnippetPaths(options: UseSnippetPathsOptions) {
  return useQuery<SnippetPathsResponse>({
    queryKey: getSnippetPathsQueryKey(options),
    queryFn: () => getSnippetPaths(options),
  });
}
